import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, Lock, Eye, EyeOff, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false && user.role === "admin") {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u?.role === "admin" || u?.role === "editor") {
        navigate("/");
      } else {
        setError("Insufficient permissions.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-panel min-h-screen flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-slate-950 rounded-md flex items-center justify-center text-white font-heading font-bold">BD</div>
          <div>
            <div className="font-heading font-semibold text-slate-950">BuildDirect Africa</div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-medium text-slate-950 mb-2">Sign in</h1>
        <p className="text-slate-500 text-sm mb-6">Manage projects, comments, and inquiries.</p>
        <form onSubmit={submit} className="space-y-3">
          {/* Email — explicit dark text + light bg for max contrast on every
              device (overrides any global dark-mode CSS). */}
          <label className="block">
            <span className="text-[11px] tracking-wider uppercase text-slate-700 font-medium mb-1.5 block">Email</span>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@builddirectafrica.com"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                data-testid="admin-email-input"
                style={{ color: "#0f172a", backgroundColor: "#ffffff", caretColor: "#0f172a" }}
                className="w-full pl-9 pr-3 py-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-base placeholder:text-slate-400"
              />
            </div>
          </label>

          {/* Password with show/hide eye icon. Same explicit colours so it
              reads clearly even when the OS or browser dark-mode style
              would otherwise dim the input. */}
          <label className="block">
            <span className="text-[11px] tracking-wider uppercase text-slate-700 font-medium mb-1.5 block">Password</span>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                spellCheck={false}
                data-testid="admin-password-input"
                style={{ color: "#0f172a", backgroundColor: "#ffffff", caretColor: "#0f172a" }}
                className="w-full pl-9 pr-10 py-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-base placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="admin-password-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-900 rounded"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-md text-sm font-medium disabled:opacity-50"
          >
            <LogIn className="w-4 h-4" /> {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-8 text-xs text-slate-400 flex items-center gap-2">
          <Lock className="w-3 h-3" /> Secure admin access
        </div>
      </div>
    </div>
  );
}
